import { apiClient } from './client';

export interface AIAction {
    id: string;
    tool: string;
    params: Record<string, any>;
    description: string;
    enabled: boolean;
}

export interface AIAutoExecutedAction {
    id: string;
    tool: string;
    params: Record<string, any>;
    description: string;
    status: 'success' | 'error' | 'pending';
    error?: string;
}

export interface AIPlan {
    planId: string;
    actions: AIAction[];
    autoExecutedActions?: AIAutoExecutedAction[];
    message?: string;
    readResults?: any[];
    conversationId?: string;
    invalidateQueries?: string[];
}

export interface AITaskResult {
    planId: string;
    results: Array<{
        actionId: string;
        status: 'success' | 'error';
        result?: any;
        error?: string;
    }>;
    summary: string;
    invalidateQueries: string[];
}

export interface AIConversationSummary {
    id: string;
    boardId: string;
    title: string | null;
    status: string;
    createdAt: string;
    updatedAt: string;
}

export interface AIConversationsResponse {
    data: AIConversationSummary[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        pages: number;
    };
}

class AIService {
    async sendPrompt(
        boardId: string,
        prompt: string,
        conversationHistory: any[] = [],
        conversationId?: string,
        uiContext?: any,
        isRetry?: boolean
    ): Promise<AIPlan> {
        const response = await apiClient.post(`/boards/${boardId}/ai/prompt`, {
            prompt,
            conversationHistory,
            conversationId,
            uiContext,
            isRetry,
        }, { timeout: 120000 });
        return response.data.data;
    }

    async confirmPlan(
        boardId: string,
        planId: string,
        enabledActionIds: string[]
    ): Promise<AITaskResult> {
        const response = await apiClient.post(`/boards/${boardId}/ai/confirm`, {
            planId,
            enabledActionIds,
        }, { timeout: 120000 });
        return response.data.data;
    }

    async getConversations(
        boardId: string,
        page = 1,
        limit = 20
    ): Promise<AIConversationsResponse> {
        const response = await apiClient.get(
            `/boards/${boardId}/ai/conversations?page=${page}&limit=${limit}`
        );
        return response.data;
    }

    async getConversationMessages(
        boardId: string,
        conversationId: string,
        page = 1,
        limit = 20
    ): Promise<any> {
        const response = await apiClient.get(
            `/boards/${boardId}/ai/conversations/${conversationId}/messages?page=${page}&limit=${limit}`
        );
        return response.data;
    }

    async deleteConversation(
        boardId: string,
        conversationId: string
    ): Promise<void> {
        await apiClient.delete(
            `/boards/${boardId}/ai/conversations/${conversationId}`
        );
    }
}

export const aiService = new AIService();
